import { useEffect } from 'react';

export const SITE_NAME = '5看料网';
export const SITE_ORIGIN = 'https://cipou.cn';

export type SEOProps = {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  ogType?: string;
  image?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Lightweight client-side SEO manager for the single-page app.
 * Sets document title, description, keywords, canonical, Open Graph,
 * Twitter Card metadata and page-scoped JSON-LD structured data.
 */
export function useSEO({
  title,
  description,
  keywords,
  path,
  ogType = 'website',
  image,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} - ${SITE_NAME}`;
    const url = `${SITE_ORIGIN}${path}`;
    document.title = fullTitle;

    upsertMeta('name', 'description', description);
    if (keywords) {
      upsertMeta('name', 'keywords', keywords);
    }
    upsertMeta('name', 'robots', 'index, follow, max-image-preview:large');
    upsertCanonical(url);

    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    if (image) {
      upsertMeta('property', 'og:image', image);
    }

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    if (image) {
      upsertMeta('name', 'twitter:image', image);
    }

    const existing = document.getElementById('page-jsonld');
    if (existing) {
      existing.remove();
    }
    if (jsonLd) {
      const script = document.createElement('script');
      script.id = 'page-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const cleanup = document.getElementById('page-jsonld');
      if (cleanup) {
        cleanup.remove();
      }
    };
  }, [title, description, keywords, path, ogType, image, jsonLd]);
}